import { useCallback, useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import moment from 'moment'
import { GlobalContext } from '../App'
import SubscriptionAgreementDocument from './SubscriptionAgreementDocument'
import API from '../services/API'
import useIsMobile from '../helpers/useIsMobile.js'

const AGREEMENT_NAME = 'AmbuNet Business Subscription Agreement'

// Lets every mounted copy of the hook below hear about a change made through
// another one (the site-wide reminder and the Billing page each hold one), so
// the reminder goes as soon as the agreement is accepted anywhere.
const STATUS_EVENT = 'ambunet:subscription-agreement-status'

// Where this organisation stands with the AmbuNet Business Subscription
// Agreement: the GET /billing/subscription-agreement payload, or null until it
// has loaded (and whenever it cannot be read).
//
// Only enable it for the tenant owner or a site administrator. Everything is
// sent with skipGlobalErrorHandler so a refusal or a server error stays in
// here: it can never swap the page for "not authorised", show the error page
// or leave a caller waiting. One fetch each time it is enabled — it never
// polls or retries; `refresh` is for an explicit user action.
export const useSubscriptionAgreementStatus = (enabled = true) => {
  const [agreementStatus, setAgreementStatus] = useState(null)
  const [loading, setLoading] = useState(false)

  const applyStatus = useCallback((next) => {
    setAgreementStatus(next ?? null)
    window.dispatchEvent(
      new CustomEvent(STATUS_EVENT, { detail: next ?? null }),
    )
  }, [])

  // Resolves to the fresh payload, or null when it could not be read (the
  // status already held is then left as it was). Never throws.
  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.get('/billing/subscription-agreement', {
        skipGlobalErrorHandler: true,
      })
      if (res.status === 200 && res.data?.status) {
        applyStatus(res.data)
        return res.data
      }
    } catch (e) {
      // Offline or timed out: the same as any other failed read.
    } finally {
      setLoading(false)
    }
    return null
  }, [applyStatus])

  useEffect(() => {
    if (!enabled) return undefined

    refresh()

    const onChanged = (event) => setAgreementStatus(event.detail ?? null)
    window.addEventListener(STATUS_EVENT, onChanged)
    return () => window.removeEventListener(STATUS_EVENT, onChanged)
  }, [enabled, refresh])

  return {
    agreementStatus: enabled ? agreementStatus : null,
    loading,
    refresh,
    applyStatus,
  }
}

// The signed-in page for one version of the agreement (the current one when
// no version is given). The trailing slash is deliberate: a path ending in
// "1.0" looks like a file name to static hosting and to the dev server's
// history fallback, which then never serve the app for it.
export const subscriptionAgreementPath = (version) =>
  version
    ? `/subscription-agreement/${encodeURIComponent(version)}/`
    : '/subscription-agreement'

const NO_CONFIRMATIONS = {
  reviewedAgreement: false,
  acceptsForOrganisation: false,
  hasAuthority: false,
}

const ROLE_MIN = 2
const ROLE_MAX = 120

const CADENCE_LABELS = { monthly: 'Monthly', annually: 'Annually' }

const formatFigure = (value) => Number(value).toLocaleString('en-GB')

// Capacity on top of what the plan itself includes, in the words the stored
// Plan Summary uses.
const extrasFor = (row) => {
  const extras = []
  if (row.additional > 0) {
    extras.push(`${formatFigure(row.additional)} additional, agreed with AmbuNet`)
  }
  if (row.purchasedSeats > 0) {
    extras.push(
      `${formatFigure(row.purchasedSeats)} extra employee seats purchased`,
    )
  }
  if (row.complimentarySeats > 0) {
    extras.push(
      `${formatFigure(row.complimentarySeats)} complimentary employee seats`,
    )
  }
  if (row.complimentary > 0) {
    extras.push(
      `${formatFigure(row.complimentary)} temporary or complimentary, granted by AmbuNet until its expiry date`,
    )
  }
  return extras
}

// What to say for each refusal the accept route documents. Anything not
// listed falls through to the generic message.
const ACCEPT_ERRORS = {
  impersonationCannotAccept:
    "The agreement cannot be accepted from an AmbuNet support session. It has to be accepted by the organisation's owner or a site administrator, signed in as themselves.",
  demoActionDisabled: 'The agreement cannot be accepted in the demo.',
  agreementSignatoryRequired:
    "Only the organisation's owner or a site administrator can accept the agreement.",
}

const GENERIC_ACCEPT_ERROR =
  'Your acceptance could not be recorded. Nothing has changed — please try again in a moment.'

// The server could not state the price on the Plan Summary (it reads it from
// the payment provider), so for the moment there is nothing to accept.
const SUMMARY_UNAVAILABLE_NOTE =
  'Your Plan Summary could not be loaded just now, so the agreement cannot be accepted at the moment. Nothing about your access is affected — please try again shortly.'

// A 409 refetch can show that there is no longer anything to accept.
const settledNotice = (fresh) => ({
  severity: 'info',
  message:
    fresh.status === 'accepted'
      ? 'The agreement has already been accepted for your organisation.'
      : 'There is no agreement for your organisation to accept.',
})

const SummaryLine = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', lineHeight: 1.3 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
)

// The Plan Summary exactly as the server generated it: package, fee, cadence
// and the six allowances. Nothing here is worked out in the browser.
const PlanSummary = ({ planSummary }) => {
  const rows = Array.isArray(planSummary?.allowances)
    ? planSummary.allowances.filter(Boolean)
    : []
  const showTotals = rows.some((row) => extrasFor(row).length > 0)

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Plan Summary
        </Typography>
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          <SummaryLine
            label="Organisation"
            value={planSummary?.organisationName || 'Not recorded'}
          />
          <SummaryLine label="Package" value={planSummary?.package?.name} />
          <SummaryLine
            label="Subscription fee"
            value={planSummary?.pricing?.text ?? 'as invoiced'}
          />
          <SummaryLine
            label="Billing cadence"
            value={CADENCE_LABELS[planSummary?.billingCadence] ?? 'as invoiced'}
          />
        </Box>
      </Box>

      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          typography: 'body2',
          '& th, & td': {
            px: 2,
            py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'left',
            verticalAlign: 'top',
          },
          '& th': {
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'text.secondary',
          },
          '& .figure': { textAlign: 'right', whiteSpace: 'nowrap' },
        }}
      >
        <thead>
          <tr>
            <th scope="col">Allowance</th>
            <th scope="col" className="figure">
              Included
            </th>
            {showTotals && (
              <th scope="col" className="figure">
                Total
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.metric}>
              <td>
                {row.label}
                {extrasFor(row).map((extra) => (
                  <Typography
                    key={extra}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    + {extra}
                  </Typography>
                ))}
              </td>
              <td className="figure">{row.includedText}</td>
              {showTotals && (
                <td className="figure">
                  <b>{row.totalText}</b>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  )
}

// Review-and-accept dialog for the AmbuNet Business Subscription Agreement
// (existing customers).
//
// It only ever opens because someone clicked "Review agreement", and it is an
// ordinary dialog: Escape, a click outside and "Not now" all close it, and
// nothing in the platform waits on the answer. Do NOT copy the blocking props
// of LegalAgreementModal here.
//
// The Plan Summary and the agreement text scroll; the role field, the three
// confirmations and the buttons live in the footer so they stay on screen
// however short the window is (the lesson from LegalAgreementModal).
//
// The browser supplies no agreement content: it shows the version and Plan
// Summary the server sent and echoes that version and fingerprint back, and
// the server re-checks both before recording anything.
//
// The footer is one of three things: the acceptance controls (canAccept); a
// note with "Try again" while the agreement is pending but the server cannot
// confirm the Plan Summary (planSummaryUnavailable); otherwise a read-only
// explanation (an AmbuNet support session, or an agreement that is already
// accepted).
//
//   open, onClose       ordinary dialog controls
//   agreementStatus     the GET /billing/subscription-agreement payload
//   loading             true while that payload is being fetched
//   onRefresh           async () => fresh payload | null (after a 409, and
//                       for "Try again" when the Plan Summary is unavailable)
//   onStatusChange      (payload) => void — the agreement was accepted, or
//                       turned out not to be required; the caller stores it
//
// Mount it as a SIBLING of whatever opens it (a MUI portal still bubbles
// React events to its React ancestors).
const SubscriptionAgreementDialog = ({
  open,
  onClose,
  agreementStatus,
  loading = false,
  onRefresh,
  onStatusChange,
}) => {
  const { setToast } = useContext(GlobalContext)
  const [role, setRole] = useState('')
  const [confirmations, setConfirmations] = useState(NO_CONFIRMATIONS)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState(null)
  const [roleError, setRoleError] = useState(false)
  // The accept route reported the Plan Summary unavailable and the refetch
  // could not say otherwise.
  const [acceptUnavailable, setAcceptUnavailable] = useState(false)
  const [retrying, setRetrying] = useState(false)

  // What is on screen is frozen while the dialog is closed, so accepting does
  // not flip the content to the "accepted" view during the closing fade.
  const [shown, setShown] = useState(agreementStatus)
  if (open && shown !== agreementStatus) setShown(agreementStatus)

  // Same small-viewport handling as LegalAgreementModal: phones and short
  // windows get the whole screen and compact chrome.
  const isPhone = useIsMobile('sm')
  const isShort = useMediaQuery('(max-height: 600px)', { noSsr: true })
  const tight = isPhone || isShort

  // Every opening is a fresh review: the confirmations never carry over.
  useEffect(() => {
    if (!open) return
    setConfirmations(NO_CONFIRMATIONS)
    setNotice(null)
    setRoleError(false)
    setAcceptUnavailable(false)
  }, [open])

  const agreement = shown?.agreement ?? null
  const planSummary = shown?.planSummary ?? null
  const organisation = planSummary?.organisationName || 'your organisation'
  // planSummaryUnavailable: the server could not confirm the price just now,
  // so the summary it sent is only a fallback — it is not shown, and while
  // the agreement is pending it cannot be accepted (canAccept is false with
  // it). Not the same as the read-only case, where canAccept is false on its
  // own.
  const summaryUnavailable =
    shown?.planSummaryUnavailable === true || acceptUnavailable
  const canAccept = shown?.canAccept === true && !summaryUnavailable
  const awaitingSummary = summaryUnavailable && shown?.status === 'pending'
  const title = `${agreement?.title ?? AGREEMENT_NAME}${
    agreement?.version ? ` (v${agreement.version})` : ''
  }`

  const trimmedRole = role.trim()
  const roleValid =
    trimmedRole.length >= ROLE_MIN && trimmedRole.length <= ROLE_MAX
  const allConfirmed =
    confirmations.reviewedAgreement &&
    confirmations.acceptsForOrganisation &&
    confirmations.hasAuthority
  const canSubmit = canAccept && roleValid && allConfirmed && !saving

  const setConfirmation = (key) => (e) =>
    setConfirmations((prev) => ({ ...prev, [key]: e.target.checked }))

  const handleAccept = async () => {
    if (!canSubmit) return

    setSaving(true)
    setNotice(null)
    setRoleError(false)

    try {
      // skipGlobalErrorHandler: every outcome is dealt with below. Left to
      // the global handler, a refusal would never settle this promise (the
      // spinner would hang) and a server error would replace the whole page.
      const res = await API.post(
        '/billing/subscription-agreement/accept',
        {
          agreementVersion: agreement?.version,
          fingerprint: shown?.fingerprint,
          acceptedByRole: trimmedRole,
          confirmations,
        },
        { skipGlobalErrorHandler: true },
      )
      const error = res.data?.error
      const errorCode = res.data?.errorCode

      if (res.status === 200) {
        onClose()
        onStatusChange?.(res.data)
        setToast?.({
          severity: 'success',
          message: `Thank you. The ${AGREEMENT_NAME} has been accepted for ${organisation}.`,
        })
        return
      }

      // The agreement or the plan moved on while this was open: show what
      // the server has now and ask for the confirmations again.
      if (
        res.status === 409 &&
        (error === 'agreementVersionChanged' || error === 'planSummaryChanged')
      ) {
        setConfirmations(NO_CONFIRMATIONS)
        const fresh = onRefresh ? await onRefresh() : null

        if (!fresh) {
          setNotice({
            severity: 'error',
            message:
              'The agreement details have changed and the latest version could not be loaded. Please close this window and try again.',
          })
        } else if (fresh.status !== 'pending') {
          setNotice(settledNotice(fresh))
        } else {
          setNotice({
            severity: 'warning',
            message:
              error === 'agreementVersionChanged'
                ? 'The agreement was updated while you were reviewing it. The latest version is now shown — please review it and confirm again.'
                : 'Your Plan Summary changed while you were reviewing it. The latest details are now shown — please review them and confirm again.',
          })
        }
        return
      }

      // The Plan Summary could not be regenerated, so nothing was recorded.
      // Stay open and say so; the refetched status decides what is offered
      // next (normally the note with "Try again").
      if (res.status === 409 && error === 'planSummaryUnavailable') {
        setConfirmations(NO_CONFIRMATIONS)
        const fresh = onRefresh ? await onRefresh() : null
        setAcceptUnavailable(!fresh)

        if (fresh && fresh.status !== 'pending') {
          setNotice(settledNotice(fresh))
        } else if (fresh && fresh.planSummaryUnavailable !== true) {
          setNotice({
            severity: 'info',
            message:
              'Your Plan Summary could not be loaded just now, so nothing was recorded. It is shown again above — please review it and confirm again.',
          })
        }
        return
      }

      // Nothing to accept after all: close, and let the caller drop the
      // reminder.
      if (res.status === 409 && error === 'agreementNotRequired') {
        onClose()
        onStatusChange?.({
          ...shown,
          status: 'notRequired',
          reason: null,
          showReminder: false,
          canAccept: false,
        })
        return
      }

      if (res.status === 400 && error === 'confirmationsRequired') {
        setConfirmations(NO_CONFIRMATIONS)
        setNotice({
          severity: 'error',
          message: 'Please tick all three confirmations to accept.',
        })
        return
      }

      if (res.status === 400 && error === 'roleRequired') {
        setRoleError(true)
        return
      }

      setNotice({
        severity: 'error',
        message:
          ACCEPT_ERRORS[error] ??
          ACCEPT_ERRORS[errorCode] ??
          GENERIC_ACCEPT_ERROR,
      })
    } catch (e) {
      setNotice({ severity: 'error', message: GENERIC_ACCEPT_ERROR })
    } finally {
      setSaving(false)
    }
  }

  // "Try again" while the Plan Summary is unavailable: one refetch per click.
  const handleRetry = async () => {
    if (retrying) return

    setRetrying(true)
    setNotice(null)
    try {
      const fresh = onRefresh ? await onRefresh() : null
      // A fresh status speaks for itself; without one the note stays.
      if (fresh) setAcceptUnavailable(false)
    } finally {
      setRetrying(false)
    }
  }

  const acceptedOn = shown?.acceptedAt
    ? moment(shown.acceptedAt).format('D MMMM YYYY')
    : null

  // Which summary an accepted organisation is looking at (planSummarySource):
  // only the one read back from the acceptance record is described as the
  // one that was accepted.
  const acceptedSummaryNote = summaryUnavailable
    ? ''
    : shown?.planSummarySource === 'accepted'
      ? ' This is the Plan Summary that was accepted.'
      : shown?.planSummarySource === 'regenerated'
        ? " The Plan Summary recorded at acceptance could not be loaded, so the one shown is for your organisation's current plan."
        : ''

  // Why there are no acceptance controls, when there are none.
  const readOnlyText = !shown
    ? null
    : shown.status === 'accepted'
      ? `Accepted for ${organisation}${
          shown.acceptedByName ? ` by ${shown.acceptedByName}` : ''
        }${acceptedOn ? ` on ${acceptedOn}` : ''}.${acceptedSummaryNote}`
      : shown.status === 'pending'
        ? "You are viewing this organisation through an AmbuNet support session, so the agreement cannot be accepted here. It has to be accepted by the organisation's owner or a site administrator, signed in as themselves."
        : 'There is no agreement for this organisation to accept.'

  const introText = !shown
    ? null
    : !canAccept
      ? null
      : shown.reason === 'planChanged'
        ? "Your organisation's plan has changed since the agreement was last accepted. Please review the updated Plan Summary and the agreement below. Nothing about your access changes if you decide to do this later."
        : shown.reason === 'newVersion'
          ? 'AmbuNet has published an updated version of the agreement. Please review it and your Plan Summary below. Nothing about your access changes if you decide to do this later.'
          : "Please review your organisation's Plan Summary and the agreement below. Nothing about your access changes if you decide to do this later."

  // Phones stack the footer; anything wider keeps the confirmations beside
  // the role field and the buttons, which costs the least height.
  const stackFooter = isPhone

  const confirmationLabels = [
    ['reviewedAgreement', `I have reviewed the ${AGREEMENT_NAME}`],
    ['acceptsForOrganisation', `I accept it on behalf of ${organisation}`],
    ['hasAuthority', `I have authority to bind ${organisation}`],
  ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Portal or not, React events still bubble to the component that
      // mounted this dialog: keep them inside it.
      onClick={(e) => e.stopPropagation()}
      fullWidth
      maxWidth="md"
      fullScreen={tight}
      aria-labelledby="subscription-agreement-dialog-title"
      PaperProps={{
        sx: {
          height: tight ? '100%' : '85vh',
          display: 'flex',
        },
      }}
    >
      <DialogTitle
        id="subscription-agreement-dialog-title"
        sx={{
          flexShrink: 0,
          ...(tight && { py: 1.25, px: 2, fontSize: '1.05rem' }),
        }}
      >
        {title}
      </DialogTitle>

      {/* The one scroll region: Plan Summary, then the agreement text. It
          keeps a usable minimum height; if even that does not fit, the dialog
          paper itself scrolls rather than clipping the footer. */}
      <DialogContent
        dividers
        sx={{
          flex: '1 1 0',
          minHeight: 120,
          overflowY: 'auto',
          px: tight ? 2 : 3,
          py: 2,
        }}
      >
        {!shown ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {loading ? (
              <CircularProgress size={28} />
            ) : (
              <Typography color="text.secondary">
                The agreement details could not be loaded. Please close this
                window and try again.
              </Typography>
            )}
          </Box>
        ) : (
          <Stack spacing={2}>
            {introText && !isShort && (
              <Typography variant="body2" color="text.secondary">
                {introText}
              </Typography>
            )}

            {summaryUnavailable ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  bgcolor: 'background.default',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Plan Summary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your Plan Summary could not be loaded just now. The
                  agreement itself is below.
                </Typography>
              </Box>
            ) : (
              planSummary && <PlanSummary planSummary={planSummary} />
            )}

            <Box>
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                gap={2}
                flexWrap="wrap"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  The agreement
                </Typography>
                <Link
                  href={subscriptionAgreementPath(agreement?.version)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Open the agreement in a new tab
                  <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                </Link>
              </Stack>
              <SubscriptionAgreementDocument
                version={agreement?.version}
                compact
              />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: tight ? 2 : 3,
          py: tight ? 1 : 2,
          flexShrink: 0,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          '& > :not(style) ~ :not(style)': { ml: 0 },
        }}
      >
        {notice && (
          <Alert
            severity={notice.severity}
            sx={{ py: 0, ...(tight && { fontSize: '0.8rem' }) }}
          >
            {notice.message}
          </Alert>
        )}

        {canAccept ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: stackFooter ? 'column' : 'row',
              alignItems: stackFooter ? 'stretch' : 'center',
              gap: stackFooter ? 1 : 2,
            }}
          >
            <Stack sx={{ flex: '1 1 0', minWidth: 0 }}>
              {confirmationLabels.map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={confirmations[key]}
                      onChange={setConfirmation(key)}
                      sx={{ py: 0.5 }}
                    />
                  }
                  label={label}
                  slotProps={{
                    typography: { variant: 'body2', sx: { lineHeight: 1.35 } },
                  }}
                  sx={{ mr: 0 }}
                />
              ))}
            </Stack>

            <Stack
              gap={1}
              sx={{ flex: stackFooter ? '0 0 auto' : '0 0 280px', minWidth: 0 }}
            >
              <TextField
                label="Your role in the organisation"
                placeholder="For example, Managing Director"
                size="small"
                fullWidth
                value={role}
                onChange={(e) => {
                  setRole(e.target.value)
                  setRoleError(false)
                }}
                error={roleError}
                helperText={
                  roleError
                    ? `Please enter your role (${ROLE_MIN} to ${ROLE_MAX} characters).`
                    : undefined
                }
                inputProps={{ maxLength: ROLE_MAX }}
                // Always raised, so the full label fits a narrow field.
                InputLabelProps={{ shrink: true }}
              />
              <Stack direction="row" gap={1}>
                <Button onClick={onClose} sx={{ flexShrink: 0 }}>
                  Not now
                </Button>
                <Button
                  variant="contained"
                  disabled={!canSubmit}
                  onClick={handleAccept}
                  sx={{ flex: 1, whiteSpace: 'nowrap' }}
                >
                  {saving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Accept agreement'
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : awaitingSummary ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: stackFooter ? 'column' : 'row',
              alignItems: stackFooter ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: stackFooter ? 1 : 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" role="status">
              {SUMMARY_UNAVAILABLE_NOTE}
            </Typography>
            <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
              <Button onClick={onClose} sx={{ flexShrink: 0 }}>
                Not now
              </Button>
              <Button
                variant="outlined"
                disabled={retrying}
                onClick={handleRetry}
                sx={{ flex: stackFooter ? 1 : 'none', whiteSpace: 'nowrap' }}
              >
                {retrying ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Try again'
                )}
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: stackFooter ? 'column' : 'row',
              alignItems: stackFooter ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: stackFooter ? 1 : 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {readOnlyText}
            </Typography>
            <Button onClick={onClose} sx={{ flexShrink: 0 }}>
              Close
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default SubscriptionAgreementDialog
